import type React from 'react';

// Why a finger on the signature pad used to scroll the page on iPhone instead of drawing (#159):
// the library already puts `touch-action: none` on its <svg>, but the pad sat inside an
// `overflow-x: auto` wrapper and its 1px border made the svg 2px wider than that wrapper, so the
// wrapper was a real nested scroller. On iOS a drag inside a scroller can be claimed by it (and
// chained to the page), the browser then cancels the pointer, and the stroke is lost. So:
//  - the wrapper owns the border and never scrolls, so there is no nested scroller at all;
//  - touch-action is declared on the wrapper too, not only on the library's svg;
//  - a non-passive touchmove listener cancels the native pan. This is what actually holds on
//    every iOS version, whatever WebKit does with touch-action. React's onTouchMove cannot do
//    it: React registers touch listeners as passive, and preventDefault() is ignored there.
// Only touchmove is cancelled, not touchstart, so a tap still blurs an open keyboard. Scrolling,
// pinch-zoom and double-tap outside the pad are untouched.
export const SIGNATURE_SURFACE_STYLE: React.CSSProperties = {
    border: '1px dashed #cbd5e0',
    borderRadius: 8,
    overflow: 'hidden',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
};

const cancelTouchMove = (e: TouchEvent) => {
    if (e.cancelable) e.preventDefault();
};

// Callback ref (React 19 runs the returned cleanup on unmount). Module-level so it is stable
// and React does not detach and re-attach it on every render.
export const blockTouchScroll = (el: HTMLElement | null) => {
    if (!el) return;
    el.addEventListener('touchmove', cancelTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', cancelTouchMove);
};
